import { useEffect, useState } from 'react'
import { Button, Callout } from './ui'

/** Evento di Chrome/Android che permette di proporre l'installazione dell'app. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'ecrm_install_dismissed'

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0)
    return Date.now() - at < 30 * 24 * 3600 * 1000
  } catch { return false }
}

const standalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
const isPhone = () => window.matchMedia('(max-width: 900px)').matches

/**
 * Proposta di installare Equal CRM sul telefono (PWA). Android: pulsante "Installa" (prompt del browser). iPhone: Safari
 * non ha il prompt, quindi si spiega "Condividi > Aggiungi a Home". Non compare se l'app è già installata o se
 * l'utente l'ha chiusa negli ultimi 30 giorni.
 */
export function InstallAppPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [visible, setVisible] = useState(() => !standalone() && !dismissedRecently() && isPhone() && isIos())

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
      if (!standalone() && !dismissedRecently()) setVisible(true)
    }
    const onInstalled = () => setVisible(false)
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  if (!visible) return null

  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch { /* solo per questa visita */ }
    setVisible(false)
  }
  const install = async () => {
    if (!deferred) return
    await deferred.prompt()
    await deferred.userChoice
    setDeferred(null)
    setVisible(false)
  }

  return (
    <Callout tone="info" icon="install_mobile" className="mb-3"
      action={<>
        {deferred && <Button variant="tonal" size="sm" onClick={() => void install()}>Installa</Button>}
        <Button variant="text" size="sm" onClick={close}>Non ora</Button>
      </>}>
      {deferred
        ? "Installa Equal CRM sul telefono: si apre come un'app, dall'icona nella schermata Home."
        : 'Per installare Equal CRM su iPhone e ricevere le notifiche: in Safari tocca Condividi e poi "Aggiungi alla schermata Home", poi apri il CRM dall’icona.'}
    </Callout>
  )
}
